import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subject } from 'rxjs';
import { ContextSelectorStep } from '@app/core/model/enum/contextSelectorStep';
import { CompanyService } from '@app/core/business/company/company.service';
import { Company } from '@app/core/business/company/company';
import { Customer } from '@app/core/business/customer/customer';
import { Order } from '@app/core/business/order/order';
import { CustomerService } from '@app/core/business/customer/customer.service';
import { OrderService } from '@app/core/business/order/order.service';
import { AuthenticationService } from '@app/core/business/user/authentication.service';
import { Meta } from '@angular/platform-browser';
import { environment } from '@env/environment';
import * as cssVars from 'css-vars-ponyfill';
import { PriceListType } from '../business/price-list-type.enum';
import { UserService } from '../business/user/user.service';
import { FrancoService } from '../business/franco/franco.service';
import { Franco } from '../business/franco/franco';
import { User } from '../business/user/user';
import { debounceTime, tap } from 'rxjs/operators';
import { FrancoStateService } from './franco-state.service';

export interface Context {
  companyCode: string;
  customerCode: string;
  codeClientKiwi: string;
  contactCode: string;
  orderCode: string;
  termsOfServiceValidated: boolean;
  activePriceListType: PriceListType;
  userCode: string;
}

export interface FullContext {
  company?: Company;
  customer?: Customer;
  order?: Order;
  franco?: Franco;
}

@Injectable()
export class ContextService {
  public context: Context;
  public context$: BehaviorSubject<Context>;

  public fullContext: FullContext;
  public fullContext$: BehaviorSubject<FullContext>;

  private availableCompanies$ = new BehaviorSubject<Company[]>(null);

private francoTrigger$ = new Subject<void>();
  deliveryDate: string;
  deliveryAdresse: string;
  public defaultDeparture: string;
  public user: User = null;

  constructor(
    private companyService: CompanyService,
    private customerService: CustomerService,
    private orderService: OrderService,
    private authenticationService: AuthenticationService,
    private metaService: Meta,
    private userService: UserService,
    private francoService: FrancoService,
    private francoState: FrancoStateService

  ) {

    this.authenticationService.isConnected$.subscribe((isConnected) => {
      if (!isConnected) {
        this.resetSelectedCompanyContext();
      }
    });

    this.userService.getUser$().subscribe(user => {
      this.user = user;
    });

    // Récupération de la valeur positionnée dans le navigateur
    const selectedPriceListType = localStorage.getItem('selectedPriceListType');

    // Définition d'une valeur par défaut
    let priceListTypeToSet = PriceListType.Net;

    // Définition si existe
    if (PriceListType[selectedPriceListType]) {
      priceListTypeToSet = PriceListType[selectedPriceListType];
    }

    localStorage.setItem('selectedPriceListType', priceListTypeToSet.toString());
    this.context = {
      companyCode: localStorage.getItem('selectedCompany'),
      customerCode: localStorage.getItem('selectedCustomer'),
      codeClientKiwi: localStorage.getItem('codeClientKiwi'),
      contactCode: localStorage.getItem('selectedContact'),
      orderCode: localStorage.getItem('selectedOrder'),
      termsOfServiceValidated:
        localStorage.getItem('termsOfServiceValidated') === 'true',
      activePriceListType: priceListTypeToSet,
      userCode: localStorage.getItem('userCode')
    };

    this.fullContext = {
      company: null,
      customer: null,
      order: null,
      franco: null
    };

    this.context$ = new BehaviorSubject(this.context);
    this.fullContext$ = new BehaviorSubject(this.fullContext);

    if (this.context.companyCode) {
      this.companyService.getCompany(this.context.companyCode).subscribe(
        company => {
          this.switchColor(company.settings.mainColor);
          this.fullContext.company = company;
        
          this.fullContext$.next(this.fullContext);
        },
        () => {
          this.resetSelectedCompanyContext();
        }
      );
    }

    if (this.context.customerCode) {
      this.customerService
        .getCustomer(this.context.companyCode, this.context.customerCode)
        .subscribe(
          customer => {
            this.fullContext.customer = customer;
            this.fullContext$.next(this.fullContext);
          },
          () => {
            this.resetSelectedCustomer();
          }
        );
    }

    if (this.context.orderCode) {
      this.orderService
        .getOrder(
          this.context.companyCode,
          this.context.customerCode,
          this.context.orderCode
        ).subscribe(
          order => {
            this.fullContext.order = order;
            this.deliveryDate = order.deliveryDate;
            this.deliveryAdresse = order.deliveryAddress.code;
            this.fullContext$.next(this.fullContext);
            this.defaultDeparture = this.fullContext?.customer?.deliveryAddresses?.find(da => da.code === this.deliveryAdresse)?.defaultDepartureLocation;
            this.francoState.refreshFranco(order, this.context.customerCode, this.context.companyCode);
          }
        )
    }
    // If the order doesn't exist anymore, remove it from context
    this.orderService.orderInvalid$.subscribe(isInvalid => {
      if (isInvalid) {
        this.resetSelectedOrder();
        this.orderService.orderInvalid$ = new BehaviorSubject(false);
      }
    });

    // Reset context when logging out
    this.userService.getUser$().subscribe(user => {
      if (user) {
        this.context = {
          ...this.context,

          userCode: user.code
        };
        this.companyService.updateAvailableCompanies(this.fullContext, this.context.userCode);
      }
    });
    // this.userService 

    // On écoute les demandes de rafraîchissement
    this.francoTrigger$.pipe(
      debounceTime(300) // Attend 300ms de silence après la dernière demande
    ).subscribe(() => {
      this.francoState.refreshFranco(this.fullContext.order, this.context.customerCode, this.context.companyCode);
    });
  }


  updateCompanyDocuments(aideALaVente = false) {
    if (this.fullContext.company) {

      this.companyService.getDocuments(this.context.companyCode, aideALaVente).subscribe(
        documents => {
          console.log(documents);
          let company = this.fullContext.company;
          company.documents.length = 0;
          company.documents = documents;
          this.fullContext$.next(this.fullContext);
        }
      );
    }
  }

/*   private executeGetFranco() {
  if (this.fullContext.order) {
      let francoInput = {
        customerId: this.context.customerCode,
        companyCode: this.fullContext?.company?.code == "PRONATURAFRANCE" ? "PRONATURA" : "VITAFRAIS",
        deliveryDate: new Date(this.fullContext.order.deliveryDate).toISOString(),
        deliveryAdress: this.fullContext.order.deliveryAddress.code,
        warehouse: ''
      }
      this.francoService.getCommonFranco(francoInput).subscribe(franco => {
        this.fullContext.franco = franco;
        this.fullContext$.next(this.fullContext);
      });
    }
  } */

  /*
   * Convert hex color to rgb to be able to apply opacity in css
   */
  private hexToRGB(hex) {
    let r, g, b;
    if (hex.length >= 6) {
      // HEX value #FFFFFF
      r = parseInt(hex.slice(1, 3), 16);
      g = parseInt(hex.slice(3, 5), 16);
      b = parseInt(hex.slice(5, 7), 16);
    } else {
      // HEX Shortcut #FFF
      r = parseInt(hex.slice(1, 2), 16);
      g = parseInt(hex.slice(2, 3), 16);
      b = parseInt(hex.slice(3, 4), 16);
    }

    return r + ', ' + g + ', ' + b;
  }


  public resetSelectedCompanyContext(): void {
    document.documentElement.style.setProperty('--company-color', '');
    document.documentElement.style.setProperty('--company-color-rgb', '');
    this.metaService.updateTag({
      name: 'theme-color',
      content: environment.defaultColor,
    });

    localStorage.removeItem('selectedCompany');
    localStorage.removeItem('selectedContact');
    localStorage.removeItem('selectedCustomer');
    localStorage.removeItem('selectedOrder');
    localStorage.removeItem('termsOfServiceValidated');
    if (this.context) {
      this.context = {
        ...this.context,
        companyCode: null,
        contactCode: null,
        customerCode: null,
        orderCode: null,
        termsOfServiceValidated: false,
        activePriceListType: null,
      };

      this.context$.next(this.context);

    }
    if (this.fullContext) {
      this.fullContext.company = null;
      this.fullContext.customer = null;
      this.fullContext.order = null;
      this.fullContext$.next(this.fullContext);
    }
  }

  public resetSelectedCustomer(): void {
    localStorage.removeItem('selectedCustomer');
    localStorage.removeItem('codeClientKiwi');
    localStorage.removeItem('selectedOrder');
    localStorage.removeItem('termsOfServiceValidated');

    this.context = {
      ...this.context,
      customerCode: null,
      codeClientKiwi: null,
      orderCode: null,
    };

    this.context$.next(this.context);

    this.fullContext.customer = null;
    this.fullContext.order = null;
    this.fullContext$.next(this.fullContext);
  }

  public resetSelectedOrder(): void {
    localStorage.removeItem('selectedOrder');
    this.context = {
      ...this.context,
      orderCode: null,
    };

    this.context$.next(this.context);
    this.fullContext = { ...this.fullContext, order: null };
    this.fullContext$.next(this.fullContext);
  }

  public resetSelectedFranco(): void {
    localStorage.removeItem('selectedFranco');
    this.context = {
      ...this.context,
    };

    this.context$.next(this.context);
    this.fullContext.franco = null;
    this.fullContext$.next(this.fullContext);
  }

  public setSelectedPriceList(priceListType: PriceListType) {

    // Mise à jour de l'utilisateur
    const user$ = this.userService.getUser();
    user$.subscribe(user => {
      if (user) {
        this.userService.putPreferences(user.code, user.preferences);
      }
    });

    // Mise à jour de la sélection locale
    localStorage.setItem('selectedPriceListType', priceListType);

    // Modification du contxte
    this.context = {
      ...this.context,
      activePriceListType: priceListType,
    };

    this.context$.next(this.context);
  }

  public setSelectedCompany(companyCode: string) {
    localStorage.setItem('selectedCompany', companyCode);
    localStorage.removeItem('selectedCustomer');
    localStorage.removeItem('selectedOrder');
    localStorage.removeItem('termsOfServiceValidated');

    // Remove histories filters from localStorage when switching company
    localStorage.removeItem('claims-history-table');
    localStorage.removeItem('delivery-notes-history-table');
    localStorage.removeItem('invoice-history-table');
    localStorage.removeItem('order-history-table');

    this.context = {
      companyCode: companyCode,
      contactCode: null,
      customerCode: null,
      codeClientKiwi: null,
      orderCode: null,
      termsOfServiceValidated: false,
      activePriceListType: null,
      userCode: localStorage.getItem('userCode')
    };

    if (this.context.companyCode && this.user) {

      const contactCompany = this.user.contactsCompanies.find(
        cc => cc.companyCode === this.context.companyCode
      );
      this.context.contactCode = contactCompany.contactCode;
      localStorage.setItem('selectedContact', contactCompany.contactCode);

    }

    this.context$.next(this.context);


    this.companyService
      .getCompany(this.context.companyCode)
      .subscribe(company => {
        this.switchColor(company.settings.mainColor);
        this.fullContext.company = company;
        this.fullContext.customer = null;
        this.fullContext.order = null;
        this.fullContext$.next(this.fullContext);
      });
  }

  public setSelectedCustomer(
    customerCode: string,
    codeClientKiwi: string,
    termsOfServiceValidated: boolean
  ) {
    // TODO
    var priceListType = PriceListType.Net;


    // Mise à jour de la sélection locale
    localStorage.setItem('selectedPriceListType', priceListType);

    localStorage.setItem('codeClientKiwi', codeClientKiwi);

    // Mise à jour du code client sélectionné
    localStorage.setItem('selectedCustomer', customerCode);

    // Mise à jour de la validation des CGU
    localStorage.setItem(
      'termsOfServiceValidated',
      termsOfServiceValidated.toString()
    );

    // Retrait de la commande sélectionnée
    localStorage.removeItem('selectedOrder');

    // Remove histories filters from localStorage when switching order
    localStorage.removeItem('claims-history-table');
    localStorage.removeItem('delivery-notes-history-table');
    localStorage.removeItem('invoice-history-table');
    localStorage.removeItem('order-history-table');

    this.context = {
      ...this.context,
      customerCode: customerCode,
      codeClientKiwi: codeClientKiwi,
      termsOfServiceValidated: termsOfServiceValidated,
      orderCode: null,
      activePriceListType: priceListType,
    };
    this.context$.next(this.context);

    this.customerService
      .getCustomer(this.context.companyCode, this.context.customerCode)
      .subscribe(customer => {
        this.fullContext.customer = customer;
        this.fullContext.order = null;
        this.fullContext$.next(this.fullContext);

      });
  }

  public setSelectedOrder(orderCode: string) {
    // Remove merge errors when order change
    if (this.context.orderCode != orderCode) {
      localStorage.removeItem('mergeErrorLines');
    }

    localStorage.setItem('selectedOrder', orderCode);

    this.context = {
      ...this.context,
      orderCode: orderCode,
    };

    this.context$.next(this.context);

    this.orderService
      .getOrder(
        this.context.companyCode,
        this.context.customerCode,
        orderCode,
        false
      )
      .subscribe(order => {
        this.fullContext = {
          ...this.fullContext,
          order: { ...order }
        };
        this.fullContext$.next(this.fullContext);

        if (order !== null) {
          const francoInput = {
            customerId: this.context.customerCode,
            companyCode: this.fullContext.company.code == "PRONATURAFRANCE" ? "PRONATURA" : "VITAFRAIS",
            deliveryDate: order.deliveryDate.substring(0, 10),
            deliveryAdress: order.deliveryAddress.code,
            warehouse: order.departureMagasin ? order.departureMagasin : ''
          }
          const franco = this.francoService.getCommonFranco(francoInput);

          if (franco !== null) {
            franco.subscribe(franco => {
              this.fullContext.franco = franco;
            });
            this.fullContext$.next(this.fullContext);
          }
        }
      });
  }

  public getFrancoUnique() {
    if (this.fullContext.order) {
      this.francoState.refreshFranco(
        this.fullContext.order, 
        this.context.customerCode, 
        this.context.companyCode
      );
    }
  }

  public setSelectedFullOrder(order: Order) {
    // Supprime merge errors si commande différente
    if (this.context.orderCode !== order.code) {
      localStorage.removeItem('mergeErrorLines');
    }

    // Mise à jour du contexte simple
    localStorage.setItem('selectedOrder', order.code);
    this.context = { ...this.context, orderCode: order.code };
    this.context$.next(this.context);

    // Création d'une nouvelle référence pour fullContext
    const newOrder = {
      ...order,
      lines: order.lines ? [...order.lines] : []
    };

    this.fullContext = {
      ...this.fullContext,
      order: {
        ...newOrder,
        lines: [...newOrder.lines]
      }
    };

    this.fullContext$.next(this.fullContext);
  }


  public validateTerms() {
    localStorage.setItem('termsOfServiceValidated', 'true');
    this.context = {
      ...this.context,
      termsOfServiceValidated: true,
    };

    this.context$.next(this.context);
  }

  // Get the current step as ContextSelectorStep
  // 0 = Company
  // 1 = Customer
  // 2 = Order
  // 3 = finished
  public getCurrentStep(): ContextSelectorStep {
    return [
      this.context.companyCode,
      this.context.customerCode,
      this.context.orderCode,
    ].filter(x => x != null).length as ContextSelectorStep;
  }

  public isFullContextComplete(): Boolean {
    return (
      ([
        this.fullContext.company,
        this.fullContext.customer,
        this.fullContext.order,
      ].filter(x => x != null).length as ContextSelectorStep) ===
      ContextSelectorStep.Finished
    );
  }

  // If Context is complete
  public isComplete(): boolean {
    return this.getCurrentStep() === ContextSelectorStep.Finished;
  }

  private switchColor(newColor) {
    const cssVarsModule = cssVars;
    const hexToRGB = this.hexToRGB;
    const setCssVar = function () {
      cssVarsModule.default({
        variables: {
          '--company-color': newColor,
          '--company-color-rgb': hexToRGB(newColor),
        },
      });
    };

    // Gestion de IE
    if (
      navigator.userAgent.indexOf('MSIE ') > -1 ||
      navigator.userAgent.indexOf('Trident/') > -1
    ) {
      setTimeout(setCssVar, 500);
    } else {
      setCssVar();
    }

    this.metaService.updateTag({
      name: 'theme-color',
      content: newColor,
    });
  }

  public isVitafrais(): boolean {
    return this.fullContext.company?.code == 'VITAFRAIS';
  }

  public isUserKiwi(): boolean {
    return this.context.codeClientKiwi !== null && this.context.codeClientKiwi !== '' && this.context.codeClientKiwi !== undefined;
  }
}

